import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CanselSvg, PasswordSvg, UserSvg } from '../../svgFile';
import { Link } from 'react-router-dom';
import { ErrorMessage, SuccessMessage } from '../common/Helper';
import {
  loginUser,
  sendOTP,
  verifyOTP,
  signupUser,
  resetPassword,
  setAuthStep,
  clearError,
  forgotPasswordSendMail,
  forgotPasswordVerifyOTP
} from '../../store/slices/authSlice';

const AuthModal = ({ isOpen = false, onClose, onSignupSuccess, initialMode = 'login' }) => {
  const dispatch = useDispatch();
  const { loading, error, currentStep: reduxStep } = useSelector((state) => state.auth);
  const [authMode, setAuthMode] = useState(initialMode);
  const [localStep, setLocalStep] = useState(1);
  const [showPassword, setShowPassword] = useState({
    loginPassword: false,
    signupPassword: false,
    signupConfirmPassword: false,
    forgotPassword: false,
    forgotConfirmPassword: false,
    resetPassword: false,
    resetConfirmPassword: false
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({
    login: false,
    sendOTP: false,
    verifyOTP: false,
    signup: false,
    resetPassword: false,
    resendOTP: false
  });
  useEffect(() => {
    if (authMode === 'signup' || authMode === 'forgotPassword') {
      setLocalStep(reduxStep);
    }
  }, [reduxStep, authMode]);
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);
  const togglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  useEffect(() => {
    if (isOpen) {
      resetForm();
      setAuthMode(initialMode);
      dispatch(clearError());
    }
  }, [isOpen, initialMode, dispatch]);
  if (error) {
    ErrorMessage(error);
    dispatch(clearError());
    setButtonLoading(prev => ({
      ...prev,
      login: false,
      sendOTP: false,
      verifyOTP: false,
      signup: false,
      resetPassword: false,
      resendOTP: false
    }));
  }
  useEffect(() => {
    if (error) {
      ErrorMessage(error);
      dispatch(clearError());
      setButtonLoading({
        login: false,
        sendOTP: false,
        verifyOTP: false,
        signup: false,
        resetPassword: false,
        resendOTP: false
      });
    }
  }, [error, dispatch]);
  const resetForm = () => {
    setLocalStep(1);
    dispatch(setAuthStep(1));
    setFormData({
      email: '',
      password: '',
      otp: '',
      confirmPassword: '',
    });
    setErrors({});
    setOtpTimer(0);
    setResendEnabled(false);
    setShowPassword({
      loginPassword: false,
      signupPassword: false,
      signupConfirmPassword: false,
      forgotPassword: false,
      forgotConfirmPassword: false,
      resetPassword: false,
      resetConfirmPassword: false
    });
    setButtonLoading({
      login: false,
      sendOTP: false,
      verifyOTP: false,
      signup: false,
      resetPassword: false,
      resendOTP: false
    });
  };
  const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
  
  setFormData(prev => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value
  }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  const switchMode = (mode) => {
    setAuthMode(mode);
    resetForm();
    dispatch(clearError());
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setButtonLoading(prev => ({ ...prev, login: true }));
    try {
      const result = await dispatch(loginUser({
        email: formData.email,
        password: formData.password
      }));
      if (loginUser.fulfilled.match(result)) {
        SuccessMessage(result.payload.message);
        setButtonLoading(prev => ({ ...prev, login: false }));
        if (onClose) {
          onClose();
        }
      }
      else if (loginUser.rejected.match(result)) {
        setButtonLoading(prev => ({ ...prev, login: false }));
      }
    } catch (error) {
      console.error("Login failed", error);
      setButtonLoading(prev => ({ ...prev, login: false }));
    }
  };
  const validateEmail = () => {
    const newErrors = {};
    if (!formData?.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    return newErrors;
  };
  const validateOTP = () => {
    const newErrors = {};
    if (!formData?.otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(formData?.otp)) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    return newErrors;
  };
  const handleSendOTP = async () => {
    const emailErrors = validateEmail();
    if (Object.keys(emailErrors).length > 0) {
      setErrors(emailErrors);
      return;
    }
    setButtonLoading(prev => ({ ...prev, sendOTP: true }));
    try {
      let result;
      if (authMode === 'signup') {
        result = await dispatch(sendOTP(formData.email));
      } else if (authMode === 'forgotPassword') {
        result = await dispatch(forgotPasswordSendMail(formData.email));
      }
      if (result?.type?.endsWith('/fulfilled')) {
        setOtpTimer(60);
        setResendEnabled(false);
        setErrors({});
        setButtonLoading(prev => ({ ...prev, sendOTP: false }));
        SuccessMessage(result.payload.message);
        if (authMode === 'signup' || authMode === 'forgotPassword') {
          setLocalStep(2);
          dispatch(setAuthStep(2));
        }
      }
      setButtonLoading(prev => ({ ...prev, sendOTP: false }));
    } catch (error) {
      console.error("Failed to send OTP", error);
      setButtonLoading(prev => ({ ...prev, sendOTP: false }));
    }
  };
  const handleResendOTP = async () => {
    setButtonLoading(prev => ({ ...prev, resendOTP: true }));
    try {
      let result;
      if (authMode === 'signup') {
        result = await dispatch(sendOTP(formData.email));
      } else if (authMode === 'forgotPassword') {
        result = await dispatch(forgotPasswordSendMail(formData.email));
      }
      if (result?.type?.endsWith('/fulfilled')) {
        setOtpTimer(60);
        setResendEnabled(false);
        setErrors({});
        setButtonLoading(prev => ({ ...prev, resendOTP: false }));
        SuccessMessage('OTP resent successfully!');
      }
    } catch (error) {
      console.error("Failed to resend OTP", error);
      setButtonLoading(prev => ({ ...prev, resendOTP: false }));
    }
  };
  const handleVerifyOTP = async () => {
    const otpErrors = validateOTP();
    if (Object.keys(otpErrors).length > 0) {
      setErrors(otpErrors);
      return;
    }
    setButtonLoading(prev => ({ ...prev, verifyOTP: true }));
    try {
      let result;
      if (authMode === 'signup') {
        result = await dispatch(verifyOTP({
          email: formData.email,
          otp: formData.otp
        }));
      } else if (authMode === 'forgotPassword') {
        result = await dispatch(forgotPasswordVerifyOTP({
          email: formData.email,
          otp: formData.otp
        }));
      }
      if (result?.type?.endsWith('/fulfilled')) {
        setErrors({});
        setButtonLoading(prev => ({ ...prev, verifyOTP: false }));
        SuccessMessage(result.payload.message);
        if (authMode === 'signup' || authMode === 'forgotPassword') {
          setLocalStep(3);
          dispatch(setAuthStep(3));
        }
      }
    } catch (error) {
      console.error("OTP verification failed", error);
      setButtonLoading(prev => ({ ...prev, verifyOTP: false }));
    }
  };
  const handleSignup = async (e) => {
    e.preventDefault();
    const passwordErrors = validatePassword();
    if (Object.keys(passwordErrors).length > 0) {
      setErrors(passwordErrors);
      return;
    }
    setButtonLoading(prev => ({ ...prev, signup: true }));
    try {
      const result = await dispatch(signupUser({
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword
      }));
      if (signupUser.fulfilled.match(result)) {
        setButtonLoading(prev => ({ ...prev, signup: false }));
        SuccessMessage(result.payload.message);
        switchMode('login');
        setFormData({
          email: '',
          password: '',
          otp: '',
          confirmPassword: '',
        });
        setErrors({});
        if (onSignupSuccess) {
          onSignupSuccess(result.payload.data);
        }
      }
    } catch (error) {
      console.error("Signup failed", error);
      setButtonLoading(prev => ({ ...prev, signup: false }));
    }
  };
  const validatePassword = (isCheck = false) => {
    const newErrors = {};
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData?.password)) {
      newErrors.password = 'Password must include uppercase, lowercase, and number';
    }
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
      if (!formData.termsAccepted && !isCheck) {
    newErrors.termsAccepted = 'You must accept the terms and conditions';
  }
  
    return newErrors;
  };
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const passwordErrors = validatePassword(true);
    if (Object.keys(passwordErrors).length > 0) {
      setErrors(passwordErrors);
      return;
    }
    setButtonLoading(prev => ({ ...prev, resetPassword: true }));
    try {
      const result = await dispatch(resetPassword({
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword
      }));
      if (resetPassword.fulfilled.match(result)) {
        setButtonLoading(prev => ({ ...prev, resetPassword: false }));
        SuccessMessage(result.payload.message);
        setTimeout(() => {
          switchMode('login');
        }, 500);
      }
    } catch (error) {
      console.error("Password reset failed", error);
      setButtonLoading(prev => ({ ...prev, resetPassword: false }));
    }
  };
  const handleSocialAuth = (provider) => {
  };
  const renderLoginForm = () => (
    <div className="signup-step-content">
      <h3 className="login-form-title mb-1">Hi, Welcome Back!</h3>
      <p className="mb-4 login-form-sub-title">
        Sign in to access your account and opportunities.
      </p>
      <div className="login-form-fields-sec">
        <div className="form-group position-relative">
          <span className="login-input-icon">
            <UserSvg />
          </span>
          <input
            type="text"
            name="email"
            className={`form-control custom-form-control ${errors?.email ? 'is-invalid' : ''}`}
            placeholder="Enter your email address"
            value={formData?.email}
            onChange={handleInputChange}
          />
          {errors.email && (
            <span className="invalid-feedback d-block mt-0">
            </span>
          )}
        </div>
        <div className="form-group position-relative password-input">
          <span className="login-input-icon">
            <PasswordSvg />
          </span>
          <input
            type={showPassword.loginPassword ? "text" : "password"}
            name="password"
            className={`form-control custom-form-control ${errors?.password ? 'is-invalid' : ''}`}
            placeholder="Password"
            value={formData?.password}
            onChange={handleInputChange}
          />
          <span className='login-input-icon right'>
            <img
              src={showPassword.loginPassword ? "/image/open-eye.png" : "/image/close-eye.png"}
              alt="toggle password"
              onClick={() => togglePassword('loginPassword')}
              style={{ cursor: "pointer" }}
              data-testid="toggle-password"
            />
          </span>
          {errors.password && (
            <span className="invalid-feedback d-block">
              {errors.password}
            </span>
          )}
        </div>
        <div className="text-end mb-3">
          <button
            type="button"
            className="btn btn-link p-0 small"
            onClick={() => switchMode('forgotPassword')}
          >
            Forgot Password?
          </button>
        </div>
        <button
          disabled={buttonLoading.login}
          className="btn gradient-btn w-100"
          onClick={handleLogin}
        >
          {buttonLoading.login ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Signing In...
            </>
          ) : (
            <>
              Sign In
              <span className="ms-2">
                <img src="images/btn-icon-white.svg" alt="" />
              </span>
            </>
          )}
        </button>
        <div className="position-relative text-center my-4">
          <span className="or-divider">or sign in with</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Link to="#" className="btn-theme-login-social facebook" onClick={() => handleSocialAuth('apple')}>
            <span className="me-2">
              <img src="/image/apple.svg" alt="" />
            </span>
            Apple Id
          </Link>
          <Link to="#" className="btn-theme-login-social google" onClick={() => handleSocialAuth('google')}>
            <span className="me-2">
              <img src="/image/google.svg" alt="" />
            </span>
            Google
          </Link>
        </div>
      </div>
    </div>
  );
  const renderSignupSteps = () => {
    switch (localStep) {
      case 1:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-1">Create Your Account</h3>
            <p className="mb-4 login-form-sub-title">
              Enter your email to get started. We'll send you a verification code.
            </p>
            {errors.submit && (
              <div className="alert alert-danger" role="alert">
                {errors.submit}
              </div>
            )}
            <div className="login-form-fields-sec">
              <div className="form-group position-relative">
                <span className="login-input-icon">
                  <UserSvg />
                </span>
                <input
                  type="email"
                  name="email"
                  className={`form-control custom-form-control ${errors.email ? 'is-invalid' : ''}`}
                  placeholder="Enter your email address"
                  value={formData?.email}
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <span className="invalid-feedback d-block">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>
            <button
              className="btn gradient-btn w-100"
              onClick={handleSendOTP}
              disabled={buttonLoading.sendOTP}
            >
              {buttonLoading.sendOTP ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Sending OTP...
                </>
              ) : (
                'Send Verification Code'
              )}
            </button>
            <div className="position-relative text-center my-4">
              <span className="or-divider">or sign up with</span>
            </div>
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn-theme-login-social facebook w-50"
                onClick={() => handleSocialAuth('apple')}
                disabled={loading}
              >
                <span className="me-2">
                  <img src="/image/apple.svg" alt="Apple" />
                </span>
                Apple
              </button>
              <button
                type="button"
                className="btn-theme-login-social google w-50"
                onClick={() => handleSocialAuth('google')}
                disabled={loading}
              >
                <span className="me-2">
                  <img src="/image/google.svg" alt="Google" />
                </span>
                Google
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-3">Verify Your Email</h3>
            <p className="mb-4 login-form-sub-title">
              We've sent a 6-digit code to <strong>{formData?.email}</strong>
            </p>
            <div className="form-group position-relative mb-4">
              <span className="login-input-icon">
                <UserSvg />
              </span>
              <input
                type="text"
                name="otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                className={`form-control custom-form-control text-center ${errors.otp ? 'is-invalid' : ''}`}
                placeholder="Enter 6-digit code"
                value={formData?.otp}
                onChange={(e) => {
                  const value = e?.target?.value.replace(/\D/g, '').slice(0, 6);
                  handleInputChange({ target: { name: 'otp', value } });
                }}
              />
              {errors.otp && (
                <div className="invalid-feedback d-block">
                  {errors.otp}
                </div>
              )}
            </div>
            <div className="otp-timer text-center mb-4">
              {otpTimer > 0 ? (
                <p className="small text-white">
                  Resend OTP in <span className="text-primary">{otpTimer}</span> seconds
                </p>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0 small"
                  onClick={handleResendOTP}
                  disabled={buttonLoading.resendOTP}
                >
                  {buttonLoading.resendOTP ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: '0.5rem', height: '0.5rem' }} role="status"></span>
                      Resending...
                    </>
                  ) : (
                    'Resend OTP'
                  )}
                </button>
              )}
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-50"
                onClick={() => {
                  setLocalStep(1);
                  dispatch(setAuthStep(1));
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="btn gradient-btn w-50"
                onClick={handleVerifyOTP}
                disabled={buttonLoading.verifyOTP}
              >
                {buttonLoading.verifyOTP ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-1">Complete Your Profile</h3>
            <p className="mb-4 login-form-sub-title">
              Fill in your details to complete registration
            </p>
            <div className="login-form-fields-sec">
              <div className="form-group position-relative">
                <span className="login-input-icon">
                  <UserSvg />
                </span>
                <input
                  type="email"
                  name="email"
                  className="form-control custom-form-control"
                  value={"ujjwal@gmail.com"}
                  readOnly
                />
                <small className="text-white">Your registered email</small>
              </div>
              <div className="form-group position-relative mb-3 password-input">
                <span className="login-input-icon">
                  <PasswordSvg />
                </span>
                <input
                  type={showPassword.signupPassword ? "text" : "password"}
                  name="password"
                  className={`form-control custom-form-control ${errors.password ? 'is-invalid' : ''}`}
                  placeholder="Create Password"
                  value={formData?.password}
                  onChange={handleInputChange}
                />
                <span className='login-input-icon right'>
                  <img
                    src={showPassword.signupPassword ? "/image/open-eye.png" : "/image/close-eye.png"}
                    alt="toggle password"
                    onClick={() => togglePassword('signupPassword')}
                    style={{ cursor: "pointer" }}
                  />
                </span>
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
              </div>
              <div className="form-group position-relative mb-4 password-input">
                <span className="login-input-icon">
                  <PasswordSvg />
                </span>
                <input
                  type={showPassword.signupConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  className={`form-control custom-form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Confirm Password"
                  value={formData?.confirmPassword}
                  onChange={handleInputChange}
                />
                <span className='login-input-icon right'>
                  <img
                    src={showPassword.signupConfirmPassword ? "/image/open-eye.png" : "/image/close-eye.png"}
                    alt="toggle password"
                    onClick={() => togglePassword('signupConfirmPassword')}
                    style={{ cursor: "pointer" }}
                  />
                </span>
                {errors.confirmPassword && (
                  <div className="invalid-feedback d-block">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>
              <div className="job-type-checkbox mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                <input
          type="checkbox"
          className={`form-check-input ${errors.termsAccepted ? 'is-invalid' : ''}`}
          id="termsCheckbox"
          checked={formData.termsAccepted || false}
          onChange={handleInputChange}
          name="termsAccepted"
          style={{marginTop: '-16px'}}
        />
        <label className="form-check-label text-white small" htmlFor="termsCheckbox">
          I agree to the <Link to="/terms-conditions" className="link-text text-decoration-underline" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy-policy" className="link-text text-decoration-underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        </label>
        {errors.termsAccepted && (
          <div className="invalid-feedback d-block">
            {errors.termsAccepted}
          </div>
        )}
      </div>
    </div>
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-50"
                onClick={() => {
                  setLocalStep(2);
                  dispatch(setAuthStep(2));
                }}
                disabled={buttonLoading.signup}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn gradient-btn w-50"
                onClick={handleSignup}
                disabled={buttonLoading.signup}
              >
                {buttonLoading.signup ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Creating Account...
                  </>
                ) : (
                  'Sign Up'
                )}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  const renderForgotPasswordSteps = () => {
    switch (localStep) {
      case 1:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-1">Reset Your Password</h3>
            <p className="mb-4 login-form-sub-title">
              Enter your email address to receive a verification code.
            </p>
            <div className="login-form-fields-sec">
              <div className="form-group position-relative">
                <span className="login-input-icon">
                  <UserSvg />
                </span>
                <input
                  type="email"
                  name="email"
                  className={`form-control custom-form-control ${errors.email ? 'is-invalid' : ''}`}
                  placeholder="Enter your email address"
                  value={formData?.email}
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <span className="invalid-feedback d-block">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>
            <button
              className="btn gradient-btn w-100"
              onClick={handleSendOTP}
              disabled={buttonLoading.sendOTP}
            >
              {buttonLoading.sendOTP ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Sending OTP...
                </>
              ) : (
                'Send Verification Code'
              )}
            </button>
          </div>
        );
      case 2:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-3">Verify Your Email</h3>
            <p className="mb-4 login-form-sub-title">
              We've sent a 6-digit code to <strong>{formData?.email}</strong>
            </p>
            <div className="form-group position-relative mb-4">
              <span className="login-input-icon">
                <UserSvg />
              </span>
              <input
                type="text"
                name="otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                className={`form-control custom-form-control text-center ${errors.otp ? 'is-invalid' : ''}`}
                placeholder="Enter 6-digit code"
                value={formData?.otp}
                onChange={(e) => {
                  const value = e?.target?.value.replace(/\D/g, '').slice(0, 6);
                  handleInputChange({ target: { name: 'otp', value } });
                }}
              />
              {errors.otp && (
                <div className="invalid-feedback d-block">
                  {errors.otp}
                </div>
              )}
            </div>
            <div className="otp-timer text-center mb-4">
              {otpTimer > 0 ? (
                <p className="small text-white">
                  Resend OTP in <span className="text-primary">{otpTimer}</span> seconds
                </p>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0 small"
                  onClick={handleResendOTP}
                  disabled={buttonLoading.resendOTP}
                >
                  {buttonLoading.resendOTP ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: '0.5rem', height: '0.5rem' }} role="status"></span>
                      Resending...
                    </>
                  ) : (
                    'Resend OTP'
                  )}
                </button>
              )}
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-50"
                onClick={() => {
                  setLocalStep(1);
                  dispatch(setAuthStep(1));
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="btn gradient-btn w-50"
                onClick={handleVerifyOTP}
                disabled={buttonLoading.verifyOTP}
              >
                {buttonLoading.verifyOTP ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="signup-step-content">
            <h3 className="login-form-title mb-1">Set New Password</h3>
            <p className="mb-4 login-form-sub-title">
              Create a new password for your account
            </p>
            <div className="login-form-fields-sec">
              <div className="form-group position-relative">
                <span className="login-input-icon">
                  <UserSvg />
                </span>
                <input
                  type="email"
                  name="email"
                  className="form-control custom-form-control"
                  value={formData.email}
                  readOnly
                />
                <small className="text-white">Your registered email</small>
              </div>
              <div className="form-group position-relative mb-3 password-input">
                <span className="login-input-icon">
                  <PasswordSvg />
                </span>
                <input
                  type={showPassword.resetPassword ? "text" : "password"}
                  name="password"
                  className={`form-control custom-form-control ${errors.password ? 'is-invalid' : ''}`}
                  placeholder="New Password"
                  value={formData?.password}
                  onChange={handleInputChange}
                />
                <span className='login-input-icon right'>
                  <img
                    src={showPassword.resetPassword ? "/image/open-eye.png" : "/image/close-eye.png"}
                    alt="toggle password"
                    onClick={() => togglePassword('resetPassword')}
                    style={{ cursor: "pointer" }}
                  />
                </span>
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
              </div>
              <div className="form-group position-relative mb-4 password-input">
                <span className="login-input-icon">
                  <PasswordSvg />
                </span>
                <input
                  type={showPassword.resetConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  className={`form-control custom-form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Confirm New Password"
                  value={formData?.confirmPassword}
                  onChange={handleInputChange}
                />
                <span className='login-input-icon right'>
                  <img
                    src={showPassword.resetConfirmPassword ? "/image/open-eye.png" : "/image/close-eye.png"}
                    alt="toggle password"
                    onClick={() => togglePassword('resetConfirmPassword')}
                    style={{ cursor: "pointer" }}
                  />
                </span>
                {errors.confirmPassword && (
                  <div className="invalid-feedback d-block">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-50"
                onClick={() => {
                  setLocalStep(2);
                  dispatch(setAuthStep(2));
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn gradient-btn w-50"
                onClick={handleResetPassword}
                disabled={buttonLoading.resetPassword}
              >
                {buttonLoading.resetPassword ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  const getCurrentForm = () => {
    switch (authMode) {
      case 'login':
        return renderLoginForm();
      case 'signup':
        return renderSignupSteps();
      case 'forgotPassword':
        return renderForgotPasswordSteps();
      default:
        return renderLoginForm();
    }
  };
  const getFooterContent = () => {
    switch (authMode) {
      case 'login':
        return (
          <p>
            Don't have an account?
            <button
              type="button"
              className="link-text text-decoration-underline btn btn-link p-0 ms-1"
              onClick={() => switchMode('signup')}
            >
              Sign Up
            </button>
          </p>
        );
      case 'signup':
         if (localStep === 3) {
        return  (<p>
              Already have an account?
              <button
                type="button"
                className="link-text text-decoration-underline btn btn-link p-0 ms-1"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>) 
      }
        return (
          <>
            <p>
              Already have an account?
              <button
                type="button"
                className="link-text text-decoration-underline btn btn-link p-0 ms-1"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>
            <p className="small mt-2">
              By signing up, you agree to our{' '}
              <Link to="/terms-conditions" target="_blank" className="link-text text-decoration-underline">
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" target="_blank" className="link-text text-decoration-underline">
                Privacy Policy
              </Link>
              .
            </p>
          </>
        );
      case 'forgotPassword':
        return (
          <p>
            Remember your password?
            <button
              type="button"
              className="link-text text-decoration-underline btn btn-link p-0 ms-1"
              onClick={() => switchMode('login')}
            >
              Back to Login
            </button>
          </p>
        );
      default:
        return null;
    }
  };
  if (!isOpen) return null;
  return (
    <div
      className={`modal ${isOpen ? 'show d-block' : 'fade'}`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered login-modal">
        <div className="modal-content">
          <div className="modal-body">
            <div className="row">
              <div className="col-lg-6">
                <div className="login-left">
                  <img src="/image/login-left.png" alt="" className='img-fluid' />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="cancel-login">
                  <button
                    type="button"
                    className="btn-login-close"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <CanselSvg />
                  </button>
                </div>
                <div className='d-flex justify-content-between flex-column w-100 h-100 logim-form-sec-main'>
                  <div className="logim-form-sec text-center mt-2">
                    {getCurrentForm()}
                  </div>
                  <div className="login-bottom-content text-center">
                    {getFooterContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;